import React, { useCallback, useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap';
import i18next from 'i18next';

import { CheckboxInput } from '../CheckboxInput';
import ChecklistScheduleTime from './checklist_schedule_time';

import config from "config";

const DAYS = [
    { name: 'monday', index: 1 },
    { name: 'tuesday', index: 2 },
    { name: 'wednesday', index: 3 },
    { name: 'thursday', index: 4 },
    { name: 'friday', index: 5 },
    { name: 'saturday', index: 6 },
    { name: 'sunday', index: 0 },
];

const TimeOfDay = ({ timeOfDay }) => (
    <span>
        {timeOfDay.label}{timeOfDay.timeOfDay && (` (${moment.unix(timeOfDay.timeOfDay).utc().format("HH:mm")})`)}
    </span>
);

const ChecklistSchedule = ({ readOnly, schedule, onScheduleUpdate }) => {
    const [showTimeModal, setShowTimeModal] = useState(false);
    const [selectedTimeLabel, setSelectedTimeLabel] = useState(null);

    moment.locale(i18next.language);

    const toggleTimeModal = (timeOfDay) => {
        setSelectedTimeLabel(timeOfDay);
        setShowTimeModal(!showTimeModal);
    };

    const dayIsChecked = useCallback((index) => {
        return schedule && schedule[moment.weekdays(true, index).toLowerCase()] === true;
    }, [schedule]);

    const toggleDayChecked = useCallback((index) => {
        if (!readOnly) {
            if (schedule == null) {
                onScheduleUpdate({ monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false });
            } else {
                let updatedSchedule = { ...schedule };
                updatedSchedule[moment.weekdays(true, index).toLowerCase()] = !dayIsChecked(index);
                onScheduleUpdate(updatedSchedule);
            }
        }
    }, [schedule, onScheduleUpdate])

    const saveTimeOfDay = useCallback((timeOfDay) => {
        if (schedule == null) {
            onScheduleUpdate({ monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false });
        } else {
            let remainingTimes = schedule.checklistScheduleTimeConfigs ? 
                schedule.checklistScheduleTimeConfigs.filter((time) => time.uid !== timeOfDay.uid).sort((a, b) => {return a.scheduleOrder - b.scheduleOrder}) : [];
            let offsetScheduleOrder = timeOfDay.scheduleOrder > remainingTimes.length ? remainingTimes.length : timeOfDay.scheduleOrder - 1;

            let timeConfigs = [
                ...remainingTimes.slice(0, offsetScheduleOrder),
                timeOfDay,
                ...remainingTimes.slice(offsetScheduleOrder)
            ]

            onScheduleUpdate({
                ...schedule,
                checklistScheduleTimeConfigs: timeConfigs.map((time, index) => ({ ...time, scheduleOrder: index, uid: index }))
            });
        }

        setSelectedTimeLabel(null);
        setShowTimeModal(false);
    }, [schedule, onScheduleUpdate]);

    const removeTimeOfDay = useCallback((index) => {     
        const timeConfigs = [...schedule.checklistScheduleTimeConfigs];
        timeConfigs.splice(index, 1);
        onScheduleUpdate({
            ...schedule,
            checklistScheduleTimeConfigs: timeConfigs,
        });
    }, [schedule, onScheduleUpdate]);

    return (
        <div className="row">

            <div className="col-sm-6">
                <div className="form-group ">
                    <label>{i18next.t('form-labels.day-of-week')}</label> 
                    <ul className="list-inline clearfix">
                        { DAYS.map(({index}) => 
                            <li key={index}>
                                <CheckboxInput
                                    input={{
                                        checked: dayIsChecked(index), 
                                        onChange: () => toggleDayChecked(index),
                                        disabled: readOnly
                                    }}
                                    label={moment.weekdaysShort(index)}
                                    className="checklist-day-inline"
                                    meta={{}}
                                />
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <div className="col-sm-6">
                <label>{i18next.t('form-labels.time-of-day')}</label> 
                <div className="row checklist-schedule-time-box">
                    <ul className="list-inline">
                    { schedule?.checklistScheduleTimeConfigs?.length > 0 && schedule.checklistScheduleTimeConfigs.map((timeOfDay, index) => 
                            <li key={index} className="pull-left">
                                { readOnly ? 
                                <div style={{margin: '2px 0px 0px'}}>
                                    <span className="checklist-schedule-time-label"><TimeOfDay timeOfDay={timeOfDay} /></span>
                                </div>
                                :
                                <span>
                                    <button className="btn btn-basic checklist-schedule-time-edit" onClick={() => toggleTimeModal(timeOfDay)}>
                                        <TimeOfDay timeOfDay={timeOfDay} />
                                    </button>
                                    <button className="btn btn-basic checklist-schedule-time-delete" onClick={() => removeTimeOfDay(index)}>
                                        <svg style={{verticalAlign: 'middle'}} className="css-19bqh2r" height="14" width="14" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path></svg>
                                    </button>
                                </span>
                                }
                            </li>
                      )
                    }
                    { !readOnly &&
                            <li className="pull-right" style={{padding: '0px !important'}}>
                                <a onClick={() => toggleTimeModal(null)}><img src={`${config.WEB_BASE_URL}/images/icon/add-button.svg`} name="add" alt={i18next.t('add')}/></a>
                            </li>
                    }
                    </ul>
                </div>
            </div>

            <Modal bsSize='large' show = { showTimeModal } onHide = { () => toggleTimeModal(null) }>
                <Modal.Header>
                    <Modal.Title>{i18next.t('form-labels.time-of-day')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ChecklistScheduleTime 
                        timeLabel={selectedTimeLabel}
                        onSubmit={saveTimeOfDay}
                        onCancel={toggleTimeModal}
                    />
                </Modal.Body>
            </Modal>

        </div>
    );
}

export default ChecklistSchedule;